import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { api, RequestError, type AdviceEntry, type AdviceInput } from '../../lib/api'

export function AdvicePage({ canManage = true }: { canManage?: boolean }) {
  const client = useQueryClient()
  const entries = useQuery({ queryKey: ['admin-advice'], queryFn: api.adminAdvice })
  const [editing, setEditing] = useState<AdviceEntry | null>(null)
  const [situation, setSituation] = useState('')
  const [advice, setAdvice] = useState('')
  const [active, setActive] = useState(true)
  const [search, setSearch] = useState('')
  const [deleting, setDeleting] = useState<number | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const reset = () => { setEditing(null); setSituation(''); setAdvice(''); setActive(true) }
  const save = useMutation({
    mutationFn: ({ id, input }: { id: number | null; input: AdviceInput }) =>
      id === null ? api.createAdvice(input) : api.updateAdvice(id, input),
    onSuccess: () => {
      reset()
      setNotice('راهنمایی ذخیره شد.')
      void client.invalidateQueries({ queryKey: ['admin-advice'] })
    },
  })
  const remove = useMutation({
    mutationFn: api.deleteAdvice,
    onSuccess: (_, id) => {
      if (editing?.id === id) reset()
      setDeleting(null)
      setNotice('راهنمایی حذف شد.')
      void client.invalidateQueries({ queryKey: ['admin-advice'] })
    },
  })
  const pending = save.isPending || remove.isPending
  const error = save.error ?? remove.error ?? entries.error
  const rows = (entries.data?.advice ?? []).filter((entry) =>
    `${entry.situation} ${entry.advice}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()))

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!situation.trim() || !advice.trim() || pending) return
    setNotice(null)
    save.reset()
    remove.reset()
    save.mutate({ id: editing?.id ?? null, input: { situation: situation.trim(), advice: advice.trim(), active } })
  }

  return (
    <div className="container stack">
      <div className="card">
        <h1>راهنمایی‌های افرا</h1>
        <p className="meta">برای هر موقعیت، راهنمایی مناسب را از تجربه افرا بنویسید. افرا هنگام گفت‌وگو با مؤسسه‌ها، راهنمایی‌های فعال و مرتبط را پیدا می‌کند.</p>
        <p className="meta">مثال‌های اولیه را بررسی و با نظر کارشناسان تکمیل کنید. برای کنار گذاشتن موقت یک راهنمایی، گزینه فعال را بردارید.</p>
      </div>
      {error && <p className="notice notice--error" role="alert">{error instanceof RequestError ? error.messageFa : 'دریافت یا ذخیره راهنمایی‌ها انجام نشد. دوباره تلاش کنید.'}</p>}
      {notice && <p className="notice notice--teach" role="status">{notice}</p>}
      {canManage && <section className="card">
        <h2>{editing ? 'ویرایش راهنمایی' : 'افزودن راهنمایی'}</h2>
        <form className="stack" onSubmit={submit}>
          <div className="field">
            <label htmlFor="advice-situation">موقعیت</label>
            <textarea id="advice-situation" value={situation} maxLength={2000} rows={3} required disabled={pending} placeholder="مثلاً مؤسسه برای نگه داشتن داوطلبان مشکل دارد." onChange={(e) => setSituation(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="advice-body">افرا چه راهنمایی کند؟</label>
            <textarea id="advice-body" value={advice} maxLength={4000} rows={5} required disabled={pending} placeholder="پیشنهادهای عملی و نکاتی را که باید به مؤسسه گفته شود بنویسید." onChange={(e) => setAdvice(e.target.value)} />
          </div>
          <label className="row" htmlFor="advice-active"><input id="advice-active" type="checkbox" checked={active} disabled={pending} onChange={(e) => setActive(e.target.checked)} />فعال در گفت‌وگوها</label>
          <div className="row">
            <button className="btn" type="submit" disabled={pending || !situation.trim() || !advice.trim()}>{save.isPending ? 'در حال ذخیره…' : 'ذخیره راهنمایی'}</button>
            {editing && <button className="btn btn--quiet" type="button" disabled={pending} onClick={() => { reset(); save.reset() }}>انصراف</button>}
          </div>
        </form>
      </section>}
      <section className="card stack">
        <h2>موقعیت‌ها و راهنمایی‌ها</h2>
        <div className="field">
          <label htmlFor="advice-search">جست‌وجو در موقعیت‌ها و راهنمایی‌ها</label>
          <input id="advice-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {entries.isLoading && <p className="muted" role="status">در حال بارگذاری…</p>}
        {entries.isError && <button className="btn btn--ghost" type="button" onClick={() => void entries.refetch()}>دوباره تلاش کن</button>}
        {entries.data && rows.length === 0 && <p className="muted">{search ? 'راهنمایی مرتبطی پیدا نشد.' : 'هنوز راهنمایی ثبت نشده.'}</p>}
        {rows.map((entry) => <article className="card stack" key={entry.id}>
          <div className="row"><span className={`badge ${entry.active ? 'badge--good' : 'badge--stored'}`}>{entry.active ? 'فعال' : 'غیرفعال'}</span></div>
          <h3>موقعیت</h3>
          <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{entry.situation}</p>
          <h3>راهنمایی افرا</h3>
          <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{entry.advice}</p>
          {canManage && <div className="row">
            <button className="btn btn--ghost btn--sm" type="button" disabled={pending} onClick={() => { setEditing(entry); setSituation(entry.situation); setAdvice(entry.advice); setActive(entry.active); setNotice(null); save.reset(); remove.reset() }}>ویرایش</button>
            <button className="btn btn--quiet btn--sm" type="button" disabled={pending} onClick={() => { setDeleting(entry.id); remove.reset() }}>حذف</button>
          </div>}
          {canManage && deleting === entry.id && <div className="notice notice--info">
            <p>این راهنمایی حذف شود؟</p>
            <div className="row">
              <button className="btn btn--sm" type="button" disabled={pending} onClick={() => remove.mutate(entry.id)}>{remove.isPending ? 'در حال حذف…' : 'بله، حذف کن'}</button>
              <button className="btn btn--quiet btn--sm" type="button" disabled={pending} onClick={() => setDeleting(null)}>انصراف</button>
            </div>
          </div>}
        </article>)}
      </section>
    </div>
  )
}
